import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Phone, Shield, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { firmInfo, practiceAreas } from '../mockData';

const Home = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1c1b] via-[#2a2c2b] to-[#1a1c1b] opacity-90"></div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl">
            {/* Rating Badge */}
            <div className="inline-flex items-center space-x-2 bg-[#3f4816]/30 backdrop-blur-sm border border-[#d9fb06]/20 rounded-full px-4 py-2 mb-8">
              <div className="flex text-[#d9fb06]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <span className="text-[#d9fb06] text-sm font-semibold">{firmInfo.rating} Google Rating</span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-tight mb-6">
              EXPERT LEGAL
              <span className="block text-[#d9fb06]">REPRESENTATION</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              MAC Law Advocates delivers professional legal solutions with integrity and client-focused service in Nairobi.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-base font-bold uppercase tracking-wide transition-all duration-300 hover:scale-105 w-full sm:w-auto">
                  Book Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={`tel:${firmInfo.phone}`}>
                <Button className="bg-transparent border-2 border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b] rounded-full px-8 py-6 text-base font-bold uppercase tracking-wide transition-all duration-300 hover:scale-105 w-full sm:w-auto">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-[#302f2c]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <Shield className="w-12 h-12 text-[#d9fb06] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
              <p className="text-gray-400 text-sm">Licensed advocates</p>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 text-[#d9fb06] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Client-Focused</h3>
              <p className="text-gray-400 text-sm">Your needs first</p>
            </div>
            <div className="text-center">
              <Award className="w-12 h-12 text-[#d9fb06] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">5.0 Rating</h3>
              <p className="text-gray-400 text-sm">Client satisfaction</p>
            </div>
            <div className="text-center">
              <Star className="w-12 h-12 text-[#d9fb06] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Proven Results</h3>
              <p className="text-gray-400 text-sm">Track record of success</p>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Preview */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">OUR PRACTICE AREAS</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive legal services tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {practiceAreas.slice(0, 6).map((area) => (
              <Card key={area.id} className="bg-[#302f2c] border-[#3f4816] p-8 hover:bg-[#3f4816] transition-all duration-300 hover:scale-105">
                <h3 className="text-xl font-bold text-[#d9fb06] mb-3">{area.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{area.description}</p>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/practice-areas">
              <Button className="bg-transparent border-2 border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b] rounded-full px-8 py-4 text-base font-bold uppercase tracking-wide transition-all duration-300">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#302f2c]">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            READY TO GET STARTED?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contact MAC Law Advocates today for professional legal representation you can trust.
          </p>
          <Link to="/contact">
            <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-12 py-6 text-lg font-bold uppercase tracking-wide transition-all duration-300 hover:scale-105">
              Schedule Your Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
